<style>
	:global(.mdc-notched-outline__notch) {
		width: 100%;
	}
</style>

<script lang="ts">
	import Button, { Label } from '@smui/button';
	import Card from '@smui/card';
	import Select, { Option } from '@smui/select';
	import Input from '@smui/textfield';
	import Icon from '@smui/textfield/icon';
	import { _ } from 'svelte-i18n';
	import FieldLoading from '../../../..//shared/components/common/Loading/FieldLoading.svelte';
	import FormLoading from '../../../..//shared/components/common/Loading/FormLoading.svelte';
	import Title from '../../../..//shared/components/common/Title.svelte';
	import Area from '../../../../shared/models/Area/Area';
	import AziendaEnte from '../../../../shared/models/AziendaEnte/AziendaEnte';
	import { hasRoleSysAdmin } from '../../../../shared/stores';
	import IconButton from '@smui/icon-button';

	export let params = {
		id: '0',
	};

	let disabled = true;
	
	let [data, loading, error] = AziendaEnte.get(params.id);
	let [dataAree, loadingAree, errorAree] = Area.getList();
	
	let logoFile: File | null = null;
    let logoSrc: string | null = null;
	let logoPreviewSrc: string | null = null;
	let removedLogo: boolean = false;

	let selectedArea: Area | undefined = undefined;
	$: selectedArea = $data.area;
	
	$: {
		if (!$data.nomeAziendaEnte || !$data.nome || !$data.cognome || !$data.email) {
			disabled = true;
		} else {
			if ($hasRoleSysAdmin) {
				if (!selectedArea?.id) {
					disabled = true;
				} else {
					disabled = false;
				}
			} else {
				disabled = false;
			}
		}
	}

	$: if ($data.id > 0) {
        loadLogo();
    }

	async function loadLogo() {
        if ($data.logoDocumentId) {
            try {
                const response = await fetch(`api/aziendaente/getlogo/${$data.id}`);
                if (response.ok) {
                    const result = await response.json();
                    if (result.response && result.response.content) {
                        logoSrc = `data:${result.response.mime};base64,${result.response.content}`;
                    }
                }
            } catch (error) {
                console.error('Error loading logo:', error);
            }
        }
    }

	function uploadLogo() {
        document.getElementById('logo-input').click();
    }

	function removeLogo() {
        logoFile = null;
        logoSrc = null;
        logoPreviewSrc = null;
        removedLogo = true;
    }

    function selectLogo() {
        const logoInput = document.getElementById('logo-input') as HTMLInputElement;
        if (logoInput.files && logoInput.files.length > 0) {
            logoFile = logoInput.files[0];
            logoPreviewSrc = URL.createObjectURL(logoFile);
        } else {
            logoFile = null;
            logoPreviewSrc = null;
        }
        logoInput.value = '';
    }

	async function onSubmit() {
        const formData = new FormData();

        for (let key in $data) {
            if ($data[key] !== null && $data[key] !== undefined && key !== 'logoDocumentId') {
                formData.append(key, $data[key]);
            }
        }

        if (logoFile) {
            formData.append('logo', logoFile);
        }

        formData.append('removedLogo', removedLogo.toString());

        try {
            const [newData, newLoading, newError] = AziendaEnte.save(formData, removedLogo);
            
            data = newData;
            loading = newLoading;
            error = newError;

            const unsubscribe = data.subscribe(value => {
                if (value) {
                    if (removedLogo) {
                        value.logoDocumentId = null;
                        logoSrc = null;
                    }
                    removedLogo = false;
                }
            });

            unsubscribe();

        } catch (err) {
            console.error('Error saving AziendaEnte:', err);
        }
    }
</script>

<Card class="md:px-8 md:py-8" padded>
	<div class="flex justify-between">
		<Title title="{$_('page.company/entity.detail.title')}" />
		<div class="flex flex-wrap sm:flex-nowrap sm:space-x-4">
			<button disabled="{disabled}" type="submit" form="form" class="btn-white h-14 w-14 sm:h-16 sm:w-16">
				<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">save</span>
			</button>
		</div>
	</div>
	{#if $loading}
		<FormLoading />
	{:else}
		<form on:submit|preventDefault="{onSubmit}" id="form" class="grid grid-cols-1 md:grid-cols-3">
			<Input
				required
				label="{$_('page.company/entity.detail.company/entity_name')}"
				bind:value="{$data.nomeAziendaEnte}"
				class="md:col-start-2"
			/>
			<Input required label="{$_('page.company/entity.detail.mm_name')}" bind:value="{$data.nome}" class="md:col-start-2" />
			<Input required label="{$_('page.company/entity.detail.mm_surname')}" bind:value="{$data.cognome}" class="md:col-start-2" />
			<Input required label="{$_('page.company/entity.detail.mm_email')}" bind:value="{$data.email}" class="md:col-start-2" />
			{#if $hasRoleSysAdmin}
				{#if $loadingAree}
					<FieldLoading customClass="md:col-start-2" />
				{:else}
					<Select
						required
						label="{$_('page.company/entity.detail.area')}"
						key="{(area) => `${(area && area?.id) || ''}`}"
						bind:value="{selectedArea}"
						class="md:col-start-2"
					>
						{#if $dataAree.response}
							{#each $dataAree.response as area (area?.id)}
								{#if area?.nomeComune}
									<Option
										value="{area}"
										on:click="{() => {
											$data.area = area;
											$data.areaId = area.id;
										}}">{area.nomeComune}</Option
									>
								{/if}
							{/each}
						{/if}
					</Select>
				{/if}
			{/if}
			<Input label="{$_('page.company/entity.detail.province')}" bind:value="{$data.provincia}" class="md:col-start-2" />
			<Input label="{$_('page.company/entity.detail.city')}" bind:value="{$data.citta}" class="md:col-start-2" />
			<Input label="{$_('page.company/entity.detail.postal_code')}" bind:value="{$data.cap}" class="md:col-start-2" />
			<Input label="{$_('page.company/entity.detail.address')}" bind:value="{$data.indirizzo}" class="md:col-start-2">
				<svelte:fragment slot="trailingIcon">
					{#if $data.id > 0}
						{#if $data.provincia && $data.citta && $data.cap && $data.indirizzoOK}
							<Icon title="{$_('page.company/entity.detail.valid_address')}" class="material-icons-outlined" style="color: green"
								>fmd_good</Icon
							>
						{:else}
							<Icon title="{$_('page.company/entity.detail.not_valid_address')}" class="material-icons" style="color: red"
								>wrong_location</Icon
							>
						{/if}
					{/if}
				</svelte:fragment>
			</Input>
			<label id="upload-container" for="logo-input" class="flex items-center space-x-2 md:col-start-2">
				<Button on:click="{uploadLogo}" type="button" variant="raised" class="w-36 p-2.5 text-base">
					<Label class="text-sm">{$_('global.select_file')}</Label>
				</Button>
				<div class="flex-grow">
					{#if logoFile?.name}
						<Input input$readonly value="{logoFile.name}" />
					{:else if $data?.logoDocumentId && !removedLogo}
						<Input input$readonly value="{$_('page.company/entity.detail.logo_exists')}" />
					{:else}
						<Input input$readonly value="{$_('global.no_file_selected')}" />
					{/if}
				</div>
				{#if ($data?.logoDocumentId && !removedLogo) || logoFile}
					<IconButton on:click={removeLogo}>
						<Icon class="material-icons">delete</Icon>
					</IconButton>
				{/if}
			</label>
			<input hidden type="file" id="logo-input" accept="image/*" on:change="{selectLogo}" />
			
			{#if (logoPreviewSrc || (logoSrc && !removedLogo))}
				<div class="flex justify-center mt-4 md:col-span-3">
					<div class="flex flex-col items-center">
						<img src={logoPreviewSrc || logoSrc} alt="Company Logo" class="object-contain max-w-xs mb-2 max-h-48" />
					</div>
				</div>
			{/if}
		</form>
	{/if}
</Card>
